import React, {
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
  type Ref,
} from 'react';
import { Stack } from 'components/v2/Stack/Stack';
import { Field, FieldUpdatePayload } from '..';
import { useQuery } from '@tanstack/react-query';
import Skeleton from '@mui/material/Skeleton';
import repeat from '../../../utils/repeat';
import { useTranslation } from 'utils/translation';
import { Dir } from '../../dirs';
import {
  fetchIntelligentFieldsForDirectories,
  updateIntelligentFieldsForDirectories,
} from '../service/api';
import Spacer from 'components/v2/Spacer/Spacer';
import Typography from '../../../components/v2/Typography/Typography';
import {
  formatIntelligenceFieldValueForDisplay,
  mapValuesToPayload,
} from '../utils';
import { useModalContent } from 'components/v2/Modal/useModalContent';
import { createColumnHelper } from '@tanstack/react-table';
import DataTable from '../../../components/v2/Data/DataTable';
import useSnackbars from '../../../components/v2/Snackbar/use-snackbars';
import { refetchActiveQueries } from '../../../utils/QueryClientProvider';
import { IntelligenceFieldsForm, type Props } from './IntelligenceFieldsForm';
import Section from '../../../components/v2/Section/Section';
import Swipeable, { SwipeableItem } from '../../../components/v2/Swipeable';

function isUserCancelledError(error: unknown): boolean {
  return (
    error !== null &&
    typeof error === 'object' &&
    (error as { userCancelled?: boolean }).userCancelled === true
  );
}

function isBulkNoFieldChangesError(error: unknown): boolean {
  return (
    error !== null &&
    typeof error === 'object' &&
    (error as { bulkNoFieldChanges?: boolean }).bulkNoFieldChanges === true
  );
}

function isBulkInlineConfirmPendingError(error: unknown): boolean {
  return (
    error !== null &&
    typeof error === 'object' &&
    (error as { bulkInlineConfirmPending?: boolean })
      .bulkInlineConfirmPending === true
  );
}

function resolveField(fields: Field[], fieldName: string): Field | undefined {
  return fields.find((f) => f.name === fieldName);
}

function resolveFieldLabel(fields: Field[], fieldName: string): string {
  const field = resolveField(fields, fieldName);
  return field?.settings?.label ?? fieldName;
}

type BulkPreviewRow = {
  name: string;
  label: React.ReactNode;
  value: React.ReactNode;
};

function multipleDirectoryIntelligenceQueryKey(dirs: Dir[]) {
  const effectiveGroups = [
    ...new Set(dirs.map((d) => d.directoryType).filter(Boolean) as string[]),
  ];
  const directoryIdsKey = dirs
    .map((d) => d.id)
    .sort()
    .join('_');
  const groupsKey = [...effectiveGroups].sort().join(',');
  return `multiple_intelligence_directory_${directoryIdsKey}_${groupsKey}`;
}

function MultipleDirectoryIntelligenceBulkConfirmSortedDirectories({
  dirs,
  values: _values,
}: {
  dirs: Dir[];
  values: FieldUpdatePayload;
}) {
  const sortedDirs = useMemo(
    () => [...dirs].sort((a, b) => a.id - b.id),
    [dirs],
  );

  return (
    <Typography variant="caption" component="span">
      {sortedDirs.map((i) => i.title).join(', ')}
    </Typography>
  );
}

function MultipleDirectoryIntelligenceBulkConfirmContent({
  dirs,
  values,
  fields,
}: {
  dirs: Dir[];
  values: FieldUpdatePayload;
  fields: Field[];
}) {
  const { t } = useTranslation();

  const previewRows: BulkPreviewRow[] = useMemo(
    () =>
      Object.keys(values)
        .filter((key) => key !== 'extra')
        .map((name) => ({
          name,
          label: <strong>{resolveFieldLabel(fields, name)}</strong>,
          value: (
            <strong>
              {formatIntelligenceFieldValueForDisplay(
                resolveField(fields, name),
                values[name],
              )}
            </strong>
          ),
        })),
    [fields, values],
  );

  const previewColumns = useMemo(() => {
    const columnHelper = createColumnHelper<BulkPreviewRow>();
    return [
      columnHelper.accessor('label', {
        header: t('intelligence_layer_bulk_confirm_column_field'),
        cell: ({ row }) => row.original.label,
      }),
      columnHelper.accessor('value', {
        header: t('intelligence_layer_bulk_confirm_column_value'),
        cell: ({ row }) => row.original.value,
      }),
    ];
  }, [t]);

  return (
    <Section padding="small">
      <Typography component="strong">
        {t('intelligence_layer_bulk_confirm_question')}
      </Typography>
      <Spacer />
      <Stack direction="column" gap={2}>
        <MultipleDirectoryIntelligenceBulkConfirmSortedDirectories
          dirs={dirs}
          values={values}
        />
        <Spacer />
        <div style={{ width: '100%', textAlign: 'left' }}>
          <DataTable
            slotProps={{
              table: {
                isFixed: true,
              },
            }}
            data={previewRows}
            columns={previewColumns}
            options={{ getRowId: (row) => row.name }}
          />
        </div>
      </Stack>
      <Spacer size={4} />
    </Section>
  );
}

export type FormMethods = {
  resetPendingValues(): void;
};

export const MultipleDirectoryIntelligenceFieldsForm = ({
  dirs,
  onSuccess,
  onError,
  formId,
  confirmationFormId,
  onConfirmRequest,
  methods,
}: {
  dirs: Dir[];
  confirmationFormId: string;
  onConfirmRequest?(values: FieldUpdatePayload): void;
  methods?: Ref<FormMethods | null>;
} & Pick<Props, 'onSuccess' | 'onError' | 'formId'>) => {
  const modal = useModalContent();
  const { t } = useTranslation();
  const snackbars = useSnackbars();
  const [pendingValues, setPendingValues] = useState<FieldUpdatePayload | null>(
    null,
  );

  useImperativeHandle(
    methods,
    () => ({
      resetPendingValues: () => {
        setPendingValues(null);
      },
    }),
    [],
  );

  const effectiveGroups = useMemo(
    () => [
      ...new Set(dirs.map((d) => d.directoryType).filter(Boolean) as string[]),
    ],
    [dirs],
  );

  const handleConfirmBulk = useCallback(async () => {
    if (!pendingValues) {
      return;
    }

    try {
      modal.setIsBusy?.(true);
      await updateIntelligentFieldsForDirectories({
        ids: dirs.map((d) => d.id),
        groups: effectiveGroups.length ? effectiveGroups : undefined,
        fields: mapValuesToPayload(pendingValues),
      });
      setPendingValues(null);
      onSuccess?.();
      void refetchActiveQueries();
    } catch (err) {
      onError?.(err, pendingValues);
    } finally {
      modal.setIsBusy?.(false);
    }
  }, [pendingValues, dirs, effectiveGroups, onSuccess, onError, modal]);

  const onErrorHandler = () => {
    modal.scrollToTop();
  };

  const handleError = (error: unknown) => {
    if (
      isUserCancelledError(error) ||
      isBulkNoFieldChangesError(error) ||
      isBulkInlineConfirmPendingError(error)
    ) {
      return;
    }
    (onError ?? onErrorHandler)();
  };

  const identifier = multipleDirectoryIntelligenceQueryKey(dirs);

  const directoriesBundleQuery = useQuery({
    queryKey: [identifier],
    queryFn: async (): Promise<[string, Field[]]> => {
      if (!dirs.length) {
        return ['', []];
      }

      const ids = dirs.map((d) => d.id);
      const fields = await fetchIntelligentFieldsForDirectories({
        ids,
        groups: effectiveGroups.length ? effectiveGroups : undefined,
      });

      return ['', fields];
    },
    enabled: dirs.length > 0 && !pendingValues,
    gcTime: 0,
    retry: false,
  });

  const showDirectoriesFieldsLoading =
    !pendingValues && dirs.length > 0 && directoriesBundleQuery.isLoading;

  const swipeIndex = pendingValues ? 1 : 0;

  return (
    <Swipeable animateHeight index={swipeIndex}>
      <SwipeableItem>
        {showDirectoriesFieldsLoading ? (
          <Section padding="small">
            {repeat(7, (i) => (
              <React.Fragment key={i}>
                <Skeleton height={16} width="200px" />
                <Skeleton height={64} />
                <Spacer size={2} />
              </React.Fragment>
            ))}
          </Section>
        ) : (
          <IntelligenceFieldsForm
            enableTitle={false}
            onlySubmitPersistableValues
            identifier={identifier}
            renderButtons={false}
            resolver={async () => {
              if (!dirs.length) {
                return ['', []];
              }

              return (
                directoriesBundleQuery.data ?? (['', []] as [string, Field[]])
              );
            }}
            persister={async (values: FieldUpdatePayload) => {
              const submittedFieldKeys = Object.keys(values).filter(
                (key) => key !== 'extra',
              );

              if (submittedFieldKeys.length === 0) {
                snackbars.add({
                  variant: 'error',
                  message: t('intelligence_layer_bulk_submit_requires_change'),
                });

                throw Object.assign(new Error('No bulk field values'), {
                  bulkNoFieldChanges: true,
                });
              }

              const staged = { ...values };
              setPendingValues(staged);
              onConfirmRequest?.(staged);
              throw Object.assign(new Error('Bulk inline confirm pending'), {
                bulkInlineConfirmPending: true,
              });
            }}
            onSuccess={onSuccess}
            onError={handleError}
            formId={formId}
          />
        )}
      </SwipeableItem>
      <SwipeableItem>
        {pendingValues ? (
          <>
            <MultipleDirectoryIntelligenceBulkConfirmContent
              dirs={dirs}
              values={pendingValues}
              fields={directoriesBundleQuery.data?.[1] ?? []}
            />
            {confirmationFormId ? (
              <form
                id={confirmationFormId}
                style={{ display: 'none' }}
                aria-hidden
                onSubmit={(e) => {
                  e.preventDefault();
                  void handleConfirmBulk();
                }}
              />
            ) : null}
          </>
        ) : null}
      </SwipeableItem>
    </Swipeable>
  );
};
